import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Search, Package } from 'lucide-react';
import { getProducts, addProduct, updateProduct, deleteProduct } from '../../services/firestoreService';
import ImageUploader from './ImageUploader';
import SmartImage from '../SmartImage';

const ProductsManagement = ({ category = 'accessories' }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productImage, setProductImage] = useState('');

  const categoryNames = {
    accessories: 'الاكسسوارات',
    parts: 'قطع الغيار',
    services: 'خدمات الصيانة'
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const data = await getProducts(category);
    setProducts(data);
    setLoading(false);
  }, [category]);

  useEffect(() => {
    loadProducts();
  }, [category, loadProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const productData = {
      name: formData.get('name'),
      price: formData.get('price'),
      description: formData.get('description'),
      image: productImage || editingProduct?.image || '📦',
      stock: formData.get('stock') || 'متوفر'
    };

    if (category === 'parts') {
      productData.brand = formData.get('brand');
    }

    if (editingProduct) {
      await updateProduct(category, editingProduct.id, productData);
    } else {
      await addProduct(category, productData);
    }

    setShowModal(false);
    setEditingProduct(null);
    setProductImage('');
    loadProducts();
  };

  const handleDelete = async (productId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      await deleteProduct(category, productId);
      loadProducts();
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductImage(product.image || '');
    setShowModal(true);
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة {categoryNames[category]}</h1>
          <p className="text-gray-600 mt-2">إجمالي المنتجات: {products.length}</p>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setProductImage('');
            setShowModal(true);
          }}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg
                   flex items-center gap-2 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          إضافة منتج جديد
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="ابحث عن منتج..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
          />
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
              <div className="p-6">
                <div className="w-full h-32 flex items-center justify-center mb-4">
                  <SmartImage 
                    src={product.image}
                    alt={product.name}
                    size="large"
                    fallback="📦"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{product.name}</h3>
                <p className="text-2xl font-bold text-yellow-600 text-center mb-4">{product.price}</p>
                {product.brand && (
                  <p className="text-sm text-gray-600 text-center mb-2">العلامة: {product.brand}</p>
                )}
                <p className="text-sm text-gray-600 text-center mb-4">{product.stock}</p>
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(product)}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg
                             flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg
                             flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد منتجات متاحة</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  اسم المنتج
                </label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingProduct?.name}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  السعر
                </label>
                <input
                  type="text"
                  name="price"
                  defaultValue={editingProduct?.price}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              {category === 'parts' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    العلامة التجارية
                  </label>
                  <input
                    type="text"
                    name="brand"
                    defaultValue={editingProduct?.brand}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  />
                </div>
              )}

              <ImageUploader
                currentImage={productImage || editingProduct?.image}
                onImageChange={setProductImage}
                label="صورة أو رمز المنتج"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  الوصف
                </label>
                <textarea
                  name="description"
                  defaultValue={editingProduct?.description}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg
                           font-medium transition-colors"
                >
                  {editingProduct ? 'حفظ التعديلات' : 'إضافة المنتج'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    setProductImage('');
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg
                           font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement;
